import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

const AudioContext = createContext();

export const AudioProvider = ({ children }) => {
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [playlist, setPlaylist] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(new Audio());

  useEffect(() => {
    const audio = audioRef.current;

    const updateProgress = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      playNext(); // Auto-play next episode
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [playlist, currentEpisode]); 

  const playEpisode = (episode, allEpisodes = []) => {
    // Robust check: Use GUID or AudioURL to ensure we are comparing the correct unique IDs
    const isSameEpisode = currentEpisode && (
        (episode.guid && currentEpisode.guid === episode.guid) || 
        (currentEpisode.audioUrl === episode.audioUrl)
    );

    if (isSameEpisode) {
      togglePlay();
      return;
    }

    // Update playlist if provided
    if (allEpisodes.length > 0) {
      setPlaylist(allEpisodes);
    }

    setCurrentEpisode(episode);
    
    // Reset and load new source
    audioRef.current.pause();
    audioRef.current.src = episode.audioUrl;
    audioRef.current.load();
    
    const playPromise = audioRef.current.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setIsPlaying(true);
        })
        .catch(error => {
          console.error("Playback failed:", error);
          setIsPlaying(false);
        });
    }
  };

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const seek = (val) => {
    const time = (val / 100) * audioRef.current.duration;
    audioRef.current.currentTime = time;
    setProgress(val);
  };

  const playNext = () => {
    if (!currentEpisode || playlist.length === 0) return;
    // Find index using unique ID
    const currentIndex = playlist.findIndex(ep => ep.audioUrl === currentEpisode.audioUrl);
    if (currentIndex !== -1 && currentIndex < playlist.length - 1) {
      playEpisode(playlist[currentIndex + 1], playlist);
    }
  };

  const playPrev = () => {
    if (!currentEpisode || playlist.length === 0) return;
    const currentIndex = playlist.findIndex(ep => ep.audioUrl === currentEpisode.audioUrl);
    if (currentIndex > 0) {
      playEpisode(playlist[currentIndex - 1], playlist);
    }
  };

  return (
    <AudioContext.Provider value={{ 
      currentEpisode, 
      isPlaying, 
      progress, 
      duration, 
      playEpisode, 
      togglePlay, 
      seek,
      playNext,
      playPrev,
      hasNext: currentEpisode && playlist.findIndex(ep => ep.audioUrl === currentEpisode.audioUrl) < playlist.length - 1,
      hasPrev: currentEpisode && playlist.findIndex(ep => ep.audioUrl === currentEpisode.audioUrl) > 0
    }}>
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => useContext(AudioContext);